import type { ButtonHTMLAttributes, ReactNode } from "react";
import { tokens, stateTransition, hairline } from "../tokens";
import type { AccentName } from "../tokens";
import { Spinner } from "./Spinner";

type Variant = "primary" | "default" | "ghost";

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
	children: ReactNode;
	variant?: Variant;
	// The state the primary action carries. Spent sparingly: one accented control per surface.
	accent?: AccentName;
	// While working the label is replaced by the indicator, never set beside it.
	loading?: boolean;
	block?: boolean;
}

// A button drawn as an outline, not a filled block, so structure stays in the hairline. The primary
// variant tints its own accent behind an accent hairline; default is neutral; ghost has no edge.
export function Button({
	children,
	variant = "default",
	accent = "blue",
	loading = false,
	block = false,
	disabled,
	style,
	...rest
}: ButtonProps) {
	const isPrimary = variant === "primary";
	const isGhost = variant === "ghost";

	const border = isPrimary
		? hairline(tokens.color.accent[accent])
		: isGhost
			? hairline("transparent")
			: hairline(tokens.color.stroke.hairline);

	const background = isPrimary
		? tokens.color.accentWash[accent]
		: isGhost
			? "transparent"
			: tokens.color.surface.raised;

	const color = isPrimary ? tokens.color.accent[accent] : tokens.color.text.primary;

	return (
		<button
			disabled={disabled || loading}
			style={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				gap: tokens.space.sm,
				minHeight: tokens.touch.min,
				width: block ? "100%" : undefined,
				padding: `0 ${tokens.space.lg}px`,
				border,
				background,
				color,
				borderRadius: tokens.radius.md,
				fontSize: tokens.size.body,
				fontWeight: tokens.weight.semibold,
				cursor: disabled || loading ? "default" : "pointer",
				opacity: disabled && !loading ? 0.5 : 1,
				transition: stateTransition(),
				...style,
			}}
			{...rest}
		>
			{loading ? <Spinner accent={isPrimary ? accent : "blue"} /> : children}
		</button>
	);
}
